import { useEffect, useState, useRef } from 'react';
import { styled } from '@mui/material/styles';
import {
  Button, IconButton, Grid, ClickAwayListener,
  TextField, Dialog, InputAdornment,
  DialogContent, DialogActions, Divider, Box, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Iconify from '../../components/Iconify';
import { useAuth } from '../../context/AuthContext';

const VIOLET = 'var(--color-primary-main)';
const VIOLET_LIGHT = '#818CF8';
const AMBER = '#F59E0B';
const BORDER = '#E2E8F0';
const SURFACE = '#F8FAFC';
const T1 = '#0F172A';
const T2 = '#475569';
const T3 = '#94A3B8';

const FieldWrapper = styled('div')(() => ({
  '& .MuiOutlinedInput-root': {
    background: SURFACE,
    borderRadius: 10,
    fontSize: '0.875rem',
    color: T1,
    height: 40,
    transition: 'all 0.18s ease',
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: '#CBD5E1' },
    '&.Mui-focused': { boxShadow: `0 0 0 3px rgba(var(--color-primary-main-rgb), 0.12)` },
    '&.Mui-focused fieldset': { borderColor: VIOLET, borderWidth: '1.5px' },
  },
  '& input::placeholder': { color: T3, opacity: 1 },
}));

const SearchDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    background: '#fff',
    border: `1px solid ${BORDER}`,
    borderRadius: 16,
    boxShadow: '0 20px 60px rgba(15,23,42,0.12)',
    // Colourful top strip on dialog too
    overflow: 'hidden',
    '&::before': {
      content: '""',
      display: 'block',
      height: 3,
      background: `linear-gradient(90deg, ${VIOLET}, ${VIOLET_LIGHT}, #0EA5E9)`,
    },
  },
}));

const ResultBtn = styled(Button)(() => ({
  width: '100%', textAlign: 'left', justifyContent: 'flex-start',
  color: T2, background: SURFACE, borderRadius: 8,
  marginBottom: 4, padding: '7px 12px',
  fontSize: '0.83rem', textTransform: 'none', fontWeight: 500, boxShadow: 'none',
  '&:hover': { background: 'rgba(var(--color-primary-main-rgb), 0.06)', color: VIOLET, boxShadow: 'none' },
}));

const dropDownStyle = { position: 'absolute', top: '0.3%', width: '100%' };

export default function Searchbar() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);

  const menuItems = authState.user.menu_items;
  const sideOptions = [];
  menuItems.forEach((m) => {
    if (m.menu_type !== 'drop_down') sideOptions.push(m);
    if (m.menu_type === 'drop_down') m.children?.forEach((c) => sideOptions.push(c));
  });

  const clearHistory = () => { localStorage.removeItem('history'); setHistory([]); };

  const goToHistory = (name, evt) => {
    const match = sideOptions.find((x) => x.name === name);
    if (match) { handleClose(); if (evt.ctrlKey || evt.metaKey) window.open(match.path, '_blank'); else navigate(match.path); }
  };

  const handleSubmit = (name, evt) => {
    const existing = JSON.parse(localStorage.getItem('history') || '[]');
    if (!existing.includes(name)) { if (existing.length >= 3) existing.shift(); existing.push(name); localStorage.setItem('history', JSON.stringify(existing)); }
    setHistory([...existing].reverse());
    const match = sideOptions.find((x) => x.name === name);
    if (match) { handleClose(); if (evt.ctrlKey || evt.metaKey) window.open(match.path, '_blank'); else navigate(match.path); }
    handleClose();
  };

  const removeItem = (val) => {
    const arr = JSON.parse(localStorage.getItem('history') || '[]').filter((v) => v !== val);
    localStorage.setItem('history', JSON.stringify(arr));
    setHistory(arr);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => { setOpen(false); setInputValue(''); setResults([]); };

  useEffect(() => { setHistory(JSON.parse(localStorage.getItem('history') || '[]')); }, []);
  useEffect(() => { setResults(inputValue ? sideOptions.filter((o) => o.name.toLowerCase().includes(inputValue.toLowerCase())) : []); }, [inputValue]); // eslint-disable-line
  useEffect(() => { if (isOpen) setTimeout(() => inputRef.current?.focus(), 80); }, [isOpen]);

  const tagStyle = {
    color: AMBER, fontSize: '0.63rem', fontWeight: 700,
    letterSpacing: '0.09em', textTransform: 'uppercase',
    display: 'flex', alignItems: 'center', gap: 5, margin: '8px 0 6px',
  };

  const renderResults = () => {
    if (inputValue) {
      if (results.length > 0) return (
        <>
          <div style={tagStyle}><Iconify icon="eva:search-fill" width={13} height={13} />Search Results</div>
          {results.map((o) => (
            <ResultBtn key={o.name} onClick={(e) => handleSubmit(o.name, e)}>
              <Iconify icon="mdi-link" width={14} height={14} style={{ marginRight: 8, color: T3 }} />{o.name}
            </ResultBtn>
          ))}
        </>
      );
      return <Typography sx={{ textAlign: 'center', color: T3, py: 3, fontSize: '0.85rem' }}>No module found</Typography>;
    }
    if (history.length > 0) return (
      <>
        <div style={{ ...tagStyle, justifyContent: 'space-between' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Iconify icon="mdi-history" width={13} height={13} />Recent</span>
          <span style={{ cursor: 'pointer', color: T3, fontSize: '0.65rem', fontWeight: 400 }} onClick={clearHistory} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') clearHistory(); }}>Clear all</span>
        </div>
        {history.map((val) => (
          <Box key={val} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ResultBtn sx={{ flex: 1 }} onClick={(e) => goToHistory(val, e)}>
              <Iconify icon="mdi-clock-outline" width={14} height={14} style={{ marginRight: 8, color: T3 }} />{val}
            </ResultBtn>
            <IconButton size="small" onClick={() => removeItem(val)} sx={{ color: T3, '&:hover': { color: '#EF4444' }, p: 0.5 }}>
              <Iconify icon="mdi-close" width={14} height={14} />
            </IconButton>
          </Box>
        ))}
      </>
    );
    return <Typography sx={{ textAlign: 'center', color: T3, py: 3, fontSize: '0.82rem' }}>Start typing to search modules…</Typography>;
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        {!isOpen && (
          <FieldWrapper>
            <TextField
              onClick={handleOpen} placeholder="Search modules…" size="small"
              sx={{ width: { xs: 160, sm: 240 }, input: { cursor: 'pointer' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" width={16} height={16} style={{ color: T3, marginLeft: 2 }} />
                  </InputAdornment>
                ),
              }}
            />
          </FieldWrapper>
        )}
        <SearchDialog onClose={handleClose} open={Boolean(isOpen)} PaperProps={{ style: dropDownStyle }}>
          <Box sx={{ p: 1.5 }}>
            <FieldWrapper>
              <TextField
                autoFocus value={inputValue} onChange={(e) => setInputValue(e.target.value)}
                placeholder="Search modules…" variant="outlined" fullWidth size="small" inputRef={inputRef}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" width={16} height={16} style={{ color: VIOLET, marginLeft: 2 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </FieldWrapper>
          </Box>
          <Divider sx={{ borderColor: BORDER }} />
          <DialogContent sx={{ py: 1.5, px: 2 }}><Grid item xs={12}>{renderResults()}</Grid></DialogContent>
          <Divider sx={{ borderColor: BORDER }} />
          <DialogActions sx={{ px: 2, py: 1 }}>
            <Button onClick={handleClose} size="small"
              sx={{ color: T3, fontSize: '0.75rem', textTransform: 'none', '&:hover': { color: VIOLET } }}>
              Close
            </Button>
          </DialogActions>
        </SearchDialog>
      </div>
    </ClickAwayListener>
  );
}
